import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useToast } from '@/hooks/useToast';
import { api, ApiError } from '@/lib/api';
import { queryClient } from '@/lib/queryClient';
import { SectionHeader, Field } from './SectionHeader';
import type { DeviceStatus } from '@/lib/types';

interface Props { device: DeviceStatus }

// AWS-style delete: the button stays disabled until the exact device name is
// typed. The server re-checks the name, so this is not just a UI guard.
export function DeleteDeviceSection({ device }: Props) {
  const [typed, setTyped] = useState('');
  const toast = useToast();
  const navigate = useNavigate();
  const name = device.client_name;
  const matches = typed === name;

  const mut = useMutation({
    mutationFn: () => api(`/api/devices/${device.id}`, { method: 'DELETE', body: { confirmName: typed } }),
    onSuccess: () => {
      toast.ok(`Device "${name}" deleted`);
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      queryClient.removeQueries({ queryKey: ['device-status', device.client_id] });
      navigate('/devices', { replace: true });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Delete failed'),
  });

  return (
    <div className="space-y-5">
      <SectionHeader title="Delete device"
        description="Removes this device from the server and from every user it is assigned to. Its cleaning history is kept. The action is recorded in the audit log." />

      <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800 space-y-1">
        <div><strong>This cannot be undone.</strong> User assignments and the license expiry for this device are lost.</div>
        {device.online && (
          <div>
            This device is <strong>online</strong>. It will register again as a new, unassigned device the next time it
            reports in (within seconds), unless it is powered off or reconfigured first.
          </div>
        )}
      </div>

      <Field label={`To confirm, type the device name: ${name}`} htmlFor="confirm-delete-name">
        <Input
          id="confirm-delete-name"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={name}
          autoComplete="off"
          spellCheck={false}
        />
      </Field>

      <Button
        variant="danger"
        onClick={() => mut.mutate()}
        disabled={!matches || mut.isPending}
        loading={mut.isPending}
      >
        <Trash2 className="w-4 h-4" /> Delete device
      </Button>
    </div>
  );
}
